import React, { useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Paper, Typography, useTheme } from "@mui/material";
import { LexicalEditor, SerializedEditorState, SerializedLexicalNode } from "lexical";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { InitialConfigType, InitialEditorStateType, LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { $generateHtmlFromNodes } from "@lexical/html";
import { ListNode, ListItemNode } from "@lexical/list";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkNode } from '@lexical/link';
// import { TreeView } from '@lexical/react/LexicalTreeView'; // uncomment for debugging tool

import "./styles.css";
import ToolbarPlugin from "./RichTextEditorToolbar/ToolbarPlugin";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";

// Lexical theme styling
const theme = {
  paragraph: 'editor-text-paragraph',
  quote: 'editor-blockquote',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
  },
  list: {
    listitem: 'editor-list-item',
    listitemUnchecked: 'editor-list-item-unchecked',
    listitemChecked: 'editor-list-item-checked',
    nested: {
      listitem: 'editor-nested-list-item',
    },
  }
};

interface RichTextEditorSettingsProps {
  widgetId: string;
  onUpdateWidgetData: any // WidgetBaseProps lists this as "any" type
  widgetRefresh: any // WidgetBaseProps lists this as "any" type - seems to be function?
  htmlContent?: string | null;
  settingsDialogIsOpen?: boolean,
  setSettingsDialogIsOpen: (settingsDialogIsOpen:boolean) => void,
  editorState?: InitialEditorStateType | undefined;
  backgroundColor?: string | undefined;
}

// Uncomment the following block to enable the debugging tool
// function TreeViewPlugin(): JSX.Element {
//   const [editor] = useLexicalComposerContext();
//   return (
//     <TreeView
//       viewClassName="tree-view-output"
//       treeTypeButtonClassName="debug-treetype-button"
//       timeTravelPanelClassName="debug-timetravel-panel"
//       timeTravelButtonClassName="debug-timetravel-button"
//       timeTravelPanelSliderClassName="debug-timetravel-panel-slider"
//       timeTravelPanelButtonClassName="debug-timetravel-panel-button"
//       editor={editor}
//     />
//   );
// }

function EditorRefPlugin({ editorRef }: { editorRef: React.MutableRefObject<LexicalEditor | null> }) {
  const [editor] = useLexicalComposerContext();
  editorRef.current = editor;

  // setTimeout is required because the editor's existence isn't known even after attempting to use useEffect.
  // This is a known issue - https://github.com/facebook/lexical/issues/4460
  setTimeout(() => {
    editor.update(() => {
      editor.getRootElement()?.focus();
    })
  });
  return null;
}

function RichTextEditorSettingsDialog(props: RichTextEditorSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const editorRef = useRef<LexicalEditor | null>(null);
  const initialEditorState: string = JSON.stringify(props.editorState);
  const [backgroundColor, setBackgroundColor] = useState<string>(props.backgroundColor ?? "unset");

  function handleSetBackgroundColor(color: string) {
    setBackgroundColor(color);
  }

  const updateWidgetData = (htmlContent: string, editorState: SerializedEditorState<SerializedLexicalNode>) => {
    if (props.widgetId && props.onUpdateWidgetData) {
      props.onUpdateWidgetData(props.widgetId, {
        htmlContent,
        editorState,
        backgroundColor
      }).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh();
        }
      });
    }
  };

  const initialConfig: InitialConfigType = {
    namespace: "EditorWidgetSettings",
    theme,
    onError: (e) => Sentry.captureException(e),
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      LinkNode,
    ],
    editorState: initialEditorState,
  };

  return (
    <ResponsiveDialog
      disableClickaway={true}
      open={!!props.settingsDialogIsOpen}
      title={i18n.t("widget-types.rich-text-editor.settings.title")}
      onClose={() => props.setSettingsDialogIsOpen(false)}
    >
      <DialogTitle>
        <Typography variant="h3">
          {i18n.t("widget-types.rich-text-editor.settings.title")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: theme.palette.background.default, p: 1 }} >
        <Paper sx={{ p: 2 }}>
        <LexicalComposer initialConfig={initialConfig}>
          <Box sx={{display: "flex", alignItems: "flex-start"}}>
            <EditorRefPlugin editorRef={editorRef} />
            <ToolbarPlugin setBackgroundColor={handleSetBackgroundColor} backgroundColor={backgroundColor} />
          </Box>
          <div style={{padding: "5px"}}>
            <RichTextPlugin
              contentEditable={<ContentEditable className="content-editable-root" style={{backgroundColor: backgroundColor}}/>}
              placeholder={<div>{i18n.t("widget-types.rich-text-editor.settings.placeholder")}</div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <ListPlugin />
          <CheckListPlugin />
          <TabIndentationPlugin />
          <HistoryPlugin />
          {/* <TreeViewPlugin /> */}
        </LexicalComposer>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          variant="contained"
          size="small"
          onClick={() => props.setSettingsDialogIsOpen(false)}
        >
          {i18n.t("cancel")}
        </Button>
        <Button
          color="success"
          variant="contained"
          size="small"
          onClick={() => {
            editorRef.current?.update(() => {
              const htmlContent: string = $generateHtmlFromNodes((editorRef.current as LexicalEditor), null);
              const editorState = (editorRef.current as LexicalEditor).getEditorState().toJSON();
              updateWidgetData(htmlContent, editorState);
              props.setSettingsDialogIsOpen(false);
            });
          }}
        >
          {i18n.t("save")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

export default RichTextEditorSettingsDialog;
